/**
 * draftStorage — Persistance localStorage du brouillon VetCase (Mode Pro Vétérinaire).
 *
 * Format de la valeur sérialisée :
 *   { schemaVersion: 1, formData: {...}, lastSavedAt: ISO_string }
 *
 * Évolution du schéma : si formData change de structure de façon incompatible
 * (ex. champ ajouté requis au load), incrémenter SCHEMA_VERSION et écarter
 * silencieusement les anciens brouillons (loadDraft retourne null).
 *
 * Clé scopée par userId pour éviter qu'un brouillon d'un véto soit lu par un
 * autre sur le même navigateur. Fallback "anonymous" si userId manquant
 * (cas dégradé : permet le test isolé).
 */

export const SCHEMA_VERSION = 1;

export function buildKey(userId) {
  const safeId = userId ? String(userId) : 'anonymous';
  return `ekynair:vetDraft:${safeId}:newCase`;
}

export function saveDraft(userId, formData) {
  try {
    const payload = {
      schemaVersion: SCHEMA_VERSION,
      formData,
      lastSavedAt: new Date().toISOString(),
    };
    localStorage.setItem(buildKey(userId), JSON.stringify(payload));
    return payload;
  } catch {
    // localStorage indisponible (Safari privé, quota, etc.) — silencieux.
    return null;
  }
}

export function loadDraft(userId) {
  try {
    const raw = localStorage.getItem(buildKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.schemaVersion !== SCHEMA_VERSION || !parsed.formData) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearDraft(userId) {
  try {
    localStorage.removeItem(buildKey(userId));
  } catch {
    // silencieux
  }
}

/**
 * Efface TOUS les brouillons de cas véto d'un utilisateur — toutes les clés
 * `ekynair:vetDraft:<userId>:*` (aujourd'hui `:newCase`, demain d'autres types).
 * Scopé par userId : les brouillons des autres utilisateurs présents sur le
 * même navigateur ne sont jamais touchés.
 *
 * Appelé à la déconnexion (RGPD — poste partagé : aucune donnée clinique ne
 * doit survivre à la fin de session).
 */
export function clearAllUserDrafts(userId) {
  try {
    const prefix = `ekynair:vetDraft:${userId ? String(userId) : 'anonymous'}:`;
    // Collecte d'abord, suppression ensuite : un removeItem pendant l'itération
    // directe décalerait les index de localStorage.key(i).
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(prefix)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    // localStorage indisponible — silencieux.
  }
}
